// Notifications and reminders in the browser (Features 1, 20 Oct): the bell's list as the board keeps it, a count
// on each board (and a folder's total) of what waits on the user there, and the pop-ups as notices are raised: one
// in the app, and the browser's own when the tab is in the background and the user allowed it.

import { kindOf } from "../../shared/boardKinds";
import { blockedSince, needsYou, questionOf, shownBlocked } from "../../shared/plan";
import { pageTitle } from "../../shared/entries";
import { cleanDismissed, DISMISS_DAYS, isDismissed, questionOfNotice, type Dismissed } from "../../shared/dismiss";
import type { Notice, NotifySettings, Reminder, Repeat } from "../../shared/notify";
import { cleanNotify } from "../../shared/notify";
import type { ActivityTo, Entry, ID } from "../../shared/types";
import { api } from "../lib/api";
import { commit } from "./actions";
import { openPlace } from "./place";
import { boardEntries, dismissToast, get, now, set, toast, useStore, type AppState } from "./store";

// a blocked step's time, and the ones the user is shown, are the plan's rules (shared/plan.ts), here for the bell
export { blockedSince, shownBlocked };

/** A notice that is dismissed, or is about a plan question that is (Round 6). */
const noticeGone = (d: Dismissed | null | undefined, n: Notice) => isDismissed(d, `n:${n.id}`) || (!!questionOfNotice(n.key) && isDismissed(d, `q:${questionOfNotice(n.key)}`));

/**
 * Everything that waits on the user on a board, each with the key that dismisses it (shared/dismiss.ts): its unread
 * notices, and on a plan, its open questions, its blocked steps and a session building it that waits on the user.
 * A dismissed one is left out until something newer happens on it (Round 6, 21 Oct).
 */
export function waitingKeys(s: AppState, boardId: ID): string[] {
  const b = s.boards.find((x) => x.id === boardId);
  if (!b) return [];
  const d = s.workspace?.dismissed;
  // a plan counts its own (its questions are notices too: counted once, as the plan counts them)
  const plan = kindOf(b) === "plan";
  const keys = s.notices.filter((n) => !n.read && n.board === boardId && !(plan && n.kind === "question") && !noticeGone(d, n)).map((n) => `n:${n.id}`);
  if (!plan) return keys;
  const entries = boardEntries(s, b.id);
  const ny = needsYou(entries);
  for (const q of ny.questions) if (!isDismissed(d, `q:${q.id}`)) keys.push(`q:${q.id}`);
  for (const p of shownBlocked(d, entries)) keys.push(`b:${p.id}`);
  if (s.runner?.sessionsOn) for (const x of s.runner.sessions ?? []) if (x.plan === b.id && x.state === "needs_you" && !isDismissed(d, `s:${x.id}`, x.at)) keys.push(`s:${x.id}`);
  return keys;
}

/** What waits on the user on a board: its unread notices, and on a plan, its questions and blocked steps. */
export function waitingOn(s: AppState, boardId: ID): number {
  return waitingKeys(s, boardId).length;
}

export const useWaitingOn = (boardId: ID | null | undefined) => useStore((s) => (boardId ? waitingOn(s, boardId) : 0));

/** A folder's total, so a closed folder still says something waits inside. */
export const useFolderWaiting = (folderId: ID) => useStore((s) => s.boards.filter((b) => b.folder === folderId).reduce((n, b) => n + waitingOn(s, b.id), 0));

/** A plan's blocked step in the notification centre: it raises no notice of its own, so the centre lists it (Round 6). */
export interface BlockedRow {
  key: string;
  board: ID;
  boardName: string;
  page: Entry;
  at: number;
}

/** Every blocked step on the user's plans that is not dismissed, newest first. */
export function blockedRows(s: AppState): BlockedRow[] {
  const d = s.workspace?.dismissed;
  const out: BlockedRow[] = [];
  for (const b of s.boards) {
    if (kindOf(b) !== "plan") continue;
    for (const p of shownBlocked(d, boardEntries(s, b.id))) out.push({ key: `b:${p.id}`, board: b.id, boardName: b.name, page: p, at: blockedSince(p) });
  }
  return out.sort((a, b) => b.at - a.at);
}

/** The notices the centre shows: every one not dismissed. */
export const shownNotices = (s: AppState) => s.notices.filter((n) => !noticeGone(s.workspace?.dismissed, n));

/** The bell's unread count: unread notices, and blocked steps, that are not dismissed. */
export const useUnread = () => useStore((s) => shownNotices(s).filter((n) => !n.read).length + blockedRows(s).length);

/** The key that dismisses a notice, and its plan question's too, when it is about one. */
export const noticeKeys = (n: Notice) => [`n:${n.id}`, ...(questionOfNotice(n.key) ? [`q:${questionOfNotice(n.key)}`] : [])];

/**
 * Dismisses notifications: off the list and the counts on every device, the things behind them as they were. Its
 * receipt takes it back. A notification comes back only when something newer happens on it (Round 6, 21 Oct).
 */
export function dismiss(keys: string[], what = keys.length === 1 ? "Dismissed" : `Dismissed ${keys.length}`, body = "Only the notification went: the step, question or task is as it was. It comes back if something new happens.") {
  if (!keys.length) return;
  // the board's clock, as the times it compares them with are
  const at = now();
  const was = get().workspace?.dismissed ?? {};
  const next = cleanDismissed({ ...was, ...Object.fromEntries(keys.map((k) => [k, at])) }, at);
  commit([{ op: "workspace.put", patch: { dismissed: next } }], "Dismiss", { history: false });
  toast({ level: "info", title: what, body, key: "dismissed", action: { label: "Undo", run: () => undismiss(keys) } });
}

/** Brings dismissed notifications back, from a receipt's Undo or the Dismissed view. */
export function undismiss(keys: string[]) {
  const was = get().workspace?.dismissed ?? {};
  const next = { ...was };
  for (const k of keys) delete next[k];
  commit([{ op: "workspace.put", patch: { dismissed: cleanDismissed(next, now()), dismissedRemove: keys } }], "Bring back", { history: false });
}

/** Everything the notification centre shows, for Dismiss all: its notices, its blocked steps, every plan's questions. */
export function allShownKeys(s: AppState): string[] {
  const keys = new Set<string>();
  for (const n of shownNotices(s)) for (const k of noticeKeys(n)) keys.add(k);
  for (const r of blockedRows(s)) keys.add(r.key);
  for (const b of s.boards) for (const k of waitingKeys(s, b.id)) keys.add(k);
  return [...keys];
}

/** What was dismissed in the last 30 days that the board still knows, newest first, for the Dismissed view. */
export function dismissedRows(s: AppState): { key: string; at: number; title: string; where: string | null }[] {
  const d = s.workspace?.dismissed ?? {};
  const out: { key: string; at: number; title: string; where: string | null }[] = [];
  const boardName = (id: ID | null | undefined) => (id ? (s.boards.find((b) => b.id === id)?.name ?? null) : null);
  const since = now() - DISMISS_DAYS * 86_400_000;
  for (const [key, at] of Object.entries(d)) {
    if (at < since) continue;
    const id = key.slice(2);
    if (key.startsWith("n:")) {
      const n = s.notices.find((x) => x.id === id);
      if (n) out.push({ key, at, title: n.title, where: boardName(n.board) });
    } else if (key.startsWith("q:") || key.startsWith("b:")) {
      const e = s.entries[id];
      if (!e) continue;
      const words = key.startsWith("q:") ? questionOf(e).text : `${pageTitle(e)} is blocked`;
      out.push({ key, at, title: words, where: boardName(e.boardId) });
    } else if (key.startsWith("s:")) {
      const x = (s.runner?.sessions ?? []).find((y) => y.id === id);
      if (x) out.push({ key, at, title: `${x.title || x.app} needs you`, where: boardName(x.plan) });
    }
  }
  return out.sort((a, b) => b.at - a.at);
}

const fail = (title: string) => (e: unknown) => toast({ level: "error", title, body: (e as Error)?.message ?? "Try again in a moment." });

export function markRead(what: { ids?: ID[]; all?: boolean; board?: ID }) {
  // read here at once; the board says so to the other tabs
  set((s) => ({ notices: s.notices.map((n) => (what.all || (what.board && n.board === what.board) || what.ids?.includes(n.id) ? { ...n, read: true } : n)) }));
  void api("/api/notices/read", { body: what }).catch(fail("That could not be marked read"));
}

/** Later, as a reminder at that time; it leaves the list meanwhile. */
export function snooze(id: ID, until: number) {
  set((s) => ({ notices: s.notices.filter((n) => n.id !== id) }));
  void api("/api/notices/snooze", { body: { id, until } }).catch(fail("That could not be snoozed"));
}

/** Done: read, and the task it is about ticked, where there is one. */
export function doneNotice(id: ID) {
  set((s) => ({ notices: s.notices.map((n) => (n.id === id ? { ...n, read: true } : n)) }));
  void api("/api/notices/done", { body: { id } }).catch(fail("That could not be ticked off"));
}

/** Opens what a notice is about, and it is read. */
export function openNotice(n: Notice) {
  if (!n.read) markRead({ ids: [n.id] });
  if (n.to) openPlace(n.to);
}

export function addReminder(r: { text: string; at: number; repeat?: Repeat | null; to?: ActivityTo | null }): Promise<Reminder> {
  return api<{ reminder: Reminder }>("/api/reminders", { body: r }).then((x) => x.reminder);
}

export function cancelReminder(id: ID) {
  set((s) => ({ reminders: s.reminders.filter((r) => r.id !== id) }));
  void api(`/api/reminders/${encodeURIComponent(id)}`, { method: "DELETE" }).catch(fail("That reminder could not be cancelled"));
}

/** "In 10 minutes", "In an hour", "Tomorrow morning" (9 on the user's clock). */
export function snoozeTimes(now = Date.now()): { label: string; at: number }[] {
  const morning = new Date(now);
  morning.setDate(morning.getDate() + 1);
  morning.setHours(9, 0, 0, 0);
  return [
    { label: "10 minutes", at: now + 10 * 60_000 },
    { label: "An hour", at: now + 60 * 60_000 },
    { label: "Tomorrow morning", at: morning.getTime() },
  ];
}

/** The board's word on its notices: the list kept, and each one just raised popped up. */
export function onNotices(ev: { notices: Notice[]; reminders: Reminder[]; raised?: Notice[] }) {
  set({ notices: ev.notices, reminders: ev.reminders });
  // a pop-up for what is dealt with now goes: a question answered in the coding session or another tab (Features 3)
  const read = new Set(ev.notices.filter((n) => n.read).map((n) => `notice:${n.id}`));
  for (const t of get().toasts) if (t.key && read.has(t.key)) dismissToast(t.id);
  for (const n of ev.raised ?? []) popUp(n);
}

function popUp(n: Notice) {
  // its pop-up dismisses it too (Round 6)
  toast({ level: n.kind === "threshold" || n.kind === "agent" || n.kind === "question" ? "warn" : "info", title: n.title, body: n.body, who: "Context Board", to: n.to, key: `notice:${n.id}`, ms: 9000, action: { label: "Dismiss", run: () => dismiss(noticeKeys(n)) } });
  const settings = cleanNotify(get().workspace?.notify);
  // the browser's own, while the tab is in the background and the user allowed it; a push covers a closed tab
  if (settings.browser && typeof document !== "undefined" && document.visibilityState !== "visible" && typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      const shown = new Notification(n.title, { body: n.body ?? undefined, tag: n.key, icon: "/favicon.svg" });
      shown.onclick = () => {
        window.focus();
        openNotice(n);
        shown.close();
      };
    } catch {
      /* a browser that shows them only from its service worker: the push covers it */
    }
  }
}

/** Which sources raise notices, quiet hours, and the browser's own pop-ups, kept on the account. */
export const useNotifySettings = () => cleanNotify(useStore((s) => s.workspace?.notify));

export function setNotify(patch: Partial<NotifySettings>) {
  const now = cleanNotify(get().workspace?.notify);
  const next = cleanNotify({ ...now, ...patch, sources: { ...now.sources, ...patch.sources }, quiet: { ...now.quiet, ...patch.quiet } });
  commit([{ op: "workspace.put", patch: { notify: next } }], "Notification settings", { history: false });
}
